import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { grupoSistemaFormSchema, ROUTER_PATHS, useLoaders } from '@/shared';
import {
  CreateGrupoSistemaParams,
  useCreateSystemGroupAndPermissions,
  useFetchModulosSistema,
  useUpdateSystemGroupPermissions,
} from '@/shared/actions/erp';
import {
  CustomTextField,
  SingleFormBoxScene,
  TabTextLabel,
} from '@/shared/components/ui';
import { gridSizeMdLg11, gridSizeMdLg8 } from '@/shared/constants';
import {
  GrupoSistema,
  ModuloSistema,
  PermissionSistema,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import ModulesSelection from './ModulesSelection';
import PermisionsSelection from './PermisionsSelection';

export interface SaveGrupoSistemaProps {
  title: string;
  gruposistema?: GrupoSistema;

  userPermissions: PermissionSistema[];
  userPermissionsSelected: PermissionSistema[];
}

type SaveFormData = CreateGrupoSistemaParams & {};

const SaveGrupoSistema: React.FC<SaveGrupoSistemaProps> = ({
  title,
  gruposistema,

  userPermissions,
  userPermissionsSelected,
}) => {
  const returnUrlGruposSistemaPage =
    ROUTER_PATHS.administration.grupoSistemaNav;
  const navigate = useNavigate();

  const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);

  ///* local state -----------------------
  const [permissions, setPermissions] = useState<PermissionSistema[]>(
    userPermissions.sort((a, b) => {
      const codenameA = a?.codename || '';
      const codenameB = b?.codename || '';

      return codenameA.localeCompare(codenameB);
    })
  );
  const [selectedPermissions, setSelectedPermissions] = useState<
    PermissionSistema[]
  >(userPermissionsSelected);

  const [systemModules, setSystemModules] = useState<ModuloSistema[]>([]);
  const [selectedSystemModules, setSelectedSystemModules] = useState<
    ModuloSistema[]
  >([]);

  ///* form -----------------------
  const form = useForm<SaveFormData>({
    resolver: yupResolver(grupoSistemaFormSchema) as any,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* fetch data -----------------------
  const {
    data: sytemModulesRes,
    isLoading: isLoadingSystemModules,
    isRefetching: isRefetchingSystemModules,
  } = useFetchModulosSistema();

  ///* mutations -----------------------
  const createGrupoSistemaMutation = useCreateSystemGroupAndPermissions({
    navigate,
    returnUrl: returnUrlGruposSistemaPage,
    enableErrorRedirect: false,
  });
  const updateGroupPermissionsMutation = useUpdateSystemGroupPermissions({
    navigate,
    returnUrl: returnUrlGruposSistemaPage,
  });

  ///* handlers -----------------------
  const onSave = async (data: SaveFormData) => {
    console.log('data', data)
   
    if (!isValid) return;

    ///* upd
    if (gruposistema?.id) {
      setIsGlobalLoading(true);
      await updateGroupPermissionsMutation.mutateAsync({
        id: gruposistema.id,
        data: {
          name: data.name,
          permissions: selectedPermissions.map(perm => perm?.id!) || [],
          modulos: selectedSystemModules,
        },
      });

      setIsGlobalLoading(false);
      return;
    }

    ///* create
    createGrupoSistemaMutation.mutate({
      name: data.name,
      permissions: selectedPermissions.map(perm => perm?.id!) || [],
      modulos: selectedSystemModules,
    });
  };

  ///* effects -----------------------
  useEffect(() => {
    if (!gruposistema?.id) return;
    reset(gruposistema);
  }, [gruposistema, reset]);
 
  useEffect(() => {
    if (isLoadingSystemModules || isRefetchingSystemModules) return;

    const modules = sytemModulesRes?.data || [];
    const selectedModules = gruposistema?.modulos || [];

    if (selectedModules.length) {
      // set selected modules
      setSelectedSystemModules(
        modules?.filter(mod => selectedModules.includes(mod))
      );

      // set all modules that are not selected
      const restModules = modules?.filter(mod => !selectedModules.includes(mod));
      setSystemModules(restModules);
      return;
    }

    setSystemModules(modules);
  }, [isLoadingSystemModules, isRefetchingSystemModules, sytemModulesRes]);

  useLoaders(isLoadingSystemModules || isRefetchingSystemModules);
  if (isLoadingSystemModules || isRefetchingSystemModules) return null;

  return (
    <SingleFormBoxScene
      titlePage={title}
      onCancel={() => navigate(returnUrlGruposSistemaPage)}
      onSave={handleSubmit(onSave, () => { })}
      formGridSize={gridSizeMdLg11}
    >
      <TabTextLabel text="Nombre del grupo" pb="1px" />
      <Grid item xs={12} container justifyContent="start" spacing={2}>
        <CustomTextField
          label="Nombre"
          name="name"
          control={form.control}
          defaultValue={form.getValues().name}
          error={errors.name}
          helperText={errors.name?.message}
          size={gridSizeMdLg8}
        />
      </Grid>

      {/* =========== MODULOS =========== */}
      <TabTextLabel text="Modulos" pt="45px" pb="6px" />
      <ModulesSelection
        modulos={systemModules}
        selectedModulos={selectedSystemModules}
        setSelectedModulos={setSelectedSystemModules}
        setModulos={setSystemModules}
      />

      {/* =========== PERMISSIONS =========== */}
      <TabTextLabel text="Permisos" pt="45px" pb="6px" />
      <PermisionsSelection
        permissions={permissions || []}
        selectedPermissions={selectedPermissions}
        setSelectedPermissions={setSelectedPermissions}
        setPermissions={setPermissions}
      />
    </SingleFormBoxScene>
  );
};

export default SaveGrupoSistema;
